import React, { useState, useEffect, useRef } from 'react';
import { SupplierPackagesProps } from './types';
import { VwpPackageItem } from '../types';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { WedPointsIcon } from '@assets/icons';

import SupplierPackageExtras from './SupplierPackageExtras';
import { useVwp } from '../VwpContext';

import scss from './SupplierPackages.module.scss';
import { WEDPOINT_EARN_RATE } from '@helper/constants';
import Slider from '@components/Slider';
import { price } from './helpers';
import { useSliderControls } from '@components/Slider/helpers';

const SupplierPackages = ({ packages, packageOrder, supplierName, changeExtra }: SupplierPackagesProps) => {
    const { selectedPackageId, onPackageSelected } = useVwp();
    const [selectedPackage, setSelectedPackage] = useState<VwpPackageItem | null>(null);
    const [wedPointsToEarn, setWedPointsToEarn] = React.useState(0);

    const sliderRef = useRef<HTMLDivElement>(null);
    const {
        isSlidesOverflown: isPackgeListOverflown,
        slideIndex,
        isPreviousEnabled,
        setSlideIndex,
        isNextEnabled,
        handlePrev,
        handleNext,
    } = useSliderControls(sliderRef, packages.length);

    useEffect(() => {
        if (!!packages && packages.length > 0) {
            if(!!selectedPackageId) {
                const selectedPackage = packages.find(p => p.id === selectedPackageId) ?? null;
                setSelectedPackage(selectedPackage);

                // Scroll the selected package into view
                const index = packages.findIndex(p => p.id === selectedPackageId);
                setSlideIndex(index > 1 ? index - 1 : 0);
            } 
            else {
                setSelectedPackage(packages[0]);
            }
        }
    }, [packages, selectedPackageId]);


    useEffect(() => {
        const extrasWedPoints = calculateExtrasWedPoints();

        setWedPointsToEarn((selectedPackage?.wedPointsToEarn ?? 0) + extrasWedPoints);
    }, [selectedPackage, packageOrder]);

    const calculateExtrasWedPoints = () => {
        return packageOrder.extras.reduce((acc, extra) => {
          const packageExtra = selectedPackage?.extras?.find(e => e.id === extra.extraId);

          if(!packageExtra) {
              console.log('Extra not found', { packageExtras: selectedPackage?.extras, extra: extra.extraId, categoryId: packageOrder.categoryId, packageItemId: packageOrder.packageItemId})
              return acc;
          }

          return acc + (extra.quantity * (packageExtra.rrpPrice * WEDPOINT_EARN_RATE));
        }, 0);
      }

    return (
        <div className={scss.packageContainer}>
            <div className={scss.packagesHeader}>
                <div className={scss.heading}>Packages:</div>
                {isPackgeListOverflown && <div className={scss.buttonContainer}>
                    <button onClick={handlePrev} disabled={!isPreviousEnabled} className={scss.swipeButton}>
                        <Icon icon={faChevronLeft} />
                    </button>
                    <button onClick={handleNext} disabled={!isNextEnabled} className={scss.swipeButton}>
                        <Icon icon={faChevronRight} />
                    </button>
                </div>}
            </div>
            <Slider className={scss.packageList} idx={slideIndex} handleIndex={setSlideIndex} gap={8} ref={sliderRef}>
                {packages.map((p) => (
                    <Button key={`pkg-${p.id}`} title={p.name} onClick={() => onPackageSelected(p.id)} className={`${scss.packageButton} ${p.id === selectedPackageId ? scss.selected : ''}`} variant='outline'>
                        <div className={scss.name}>{p.name}</div>
                        <div className={scss.price}>
                            <span>{price(p.rrpPrice, false)}</span>
                            <span className={scss.packageRadioButton}></span>
                        </div>
                        
                    </Button>
                ))}
            </Slider>
            {!!selectedPackage?.inclusions && 
                <div className={scss.inclusions}>
                    <div className={scss.inclusionsHeading}>Inclusions:</div>
                    <div className={scss.inclusionsList} dangerouslySetInnerHTML={{ __html: selectedPackage.inclusions }} />
                </div>
            }
            {!!selectedPackage?.wedPointsToEarn && 
                <div className={scss.wedPoints}>
                    <WedPointsIcon />
                    <div>Book & Earn <strong>{Math.round(wedPointsToEarn).toLocaleString('en-AU')} WedPoints</strong></div>
                </div> 
            }
            {!!selectedPackage?.extras && selectedPackage.extras.length > 0 &&
                <div className={scss.extras}>
                    <div className={scss.extrasHeading}>Optional Extras:</div>
                    <SupplierPackageExtras extras={selectedPackage.extras} packageOrder={packageOrder} packageName={selectedPackage.name ?? ''} supplierName={supplierName} changeExtra={changeExtra} />
                </div>
            }
        </div>
    );
}

export default SupplierPackages;